import api from "./api";

import { VersionChangeType } from "../utils/semver";

// --- Types ---

export type LegalDocumentType =
  | "TERMS_AND_CONDITIONS"
  | "PRIVACY_POLICY"
  | "MEDICAL_DISCLAIMER";

export type LegalDocumentStatus = "DRAFT" | "PUBLISHED" | "ARCHIVED";

export interface LegalDocument {
  id: string;
  documentType: LegalDocumentType;
  version: string | number;
  changeType?: VersionChangeType | null;
  title: string;
  content: string;
  status: LegalDocumentStatus;
  isMandatory: boolean;
  requiresReAcceptance: boolean;
  effectiveFrom?: string | null;
  createdBy?: string | null;
  updatedBy?: string | null;
  createdByName?: string | null;
  updatedByName?: string | null;
  publishedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface LegalDocumentListResponse {
  documents: LegalDocument[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface LegalDocumentListParams {
  page?: number;
  limit?: number;
  documentType?: LegalDocumentType;
  status?: LegalDocumentStatus;
  search?: string;
}

export interface LegalDocumentPayload {
  documentType?: LegalDocumentType;
  title?: string;
  content?: string;
  version?: string;
  changeType?: VersionChangeType | null;
  isMandatory?: boolean;
  requiresReAcceptance?: boolean;
  effectiveFrom?: string | null;
}

// --- Constants ---

export const LEGAL_DOCUMENT_TYPES: { value: LegalDocumentType; label: string }[] = [
  { value: "TERMS_AND_CONDITIONS", label: "Terms & Conditions" },
  { value: "PRIVACY_POLICY", label: "Privacy Policy" },
  { value: "MEDICAL_DISCLAIMER", label: "Medical Disclaimer" },
];

const BASE = "/api/super-admin/legal-documents";

// --- API calls ---

export const listLegalDocuments = async (
  params?: LegalDocumentListParams
): Promise<LegalDocumentListResponse> => {
  const { data } = await api.get(BASE, { params });
  return data.data;
};

export const getLegalDocument = async (id: string): Promise<LegalDocument> => {
  const { data } = await api.get(`${BASE}/${id}`);
  return data.data;
};

export const getLegalDocumentHistory = async (
  type: LegalDocumentType
): Promise<LegalDocument[]> => {
  const { data } = await api.get(`${BASE}/${type}/history`);
  return data.data.documents;
};

export const createLegalDocument = async (
  payload: LegalDocumentPayload
): Promise<LegalDocument> => {
  const { data } = await api.post(BASE, payload);
  return data.data;
};

export const updateLegalDocument = async (
  id: string,
  payload: LegalDocumentPayload
): Promise<LegalDocument> => {
  const { data } = await api.put(`${BASE}/${id}`, payload);
  return data.data;
};

export const publishLegalDocument = async (
  id: string,
  payload?: { changeType?: VersionChangeType }
): Promise<LegalDocument> => {
  const { data } = await api.post(`${BASE}/${id}/publish`, payload || {});
  return data.data;
};

export const archiveLegalDocument = async (id: string): Promise<LegalDocument> => {
  const { data } = await api.post(`${BASE}/${id}/archive`);
  return data.data;
};

// --- User-facing (published only, backend enforces PUBLISHED) ---

const USER_BASE = "/api/legal-documents";

export const listPublishedLegalDocuments = async (): Promise<LegalDocument[]> => {
  const { data } = await api.get(USER_BASE);
  return data.data.documents;
};

export type LegalAcceptanceAction = "ACCEPTED" | "ACKNOWLEDGED";

export const getRequiredLegalDocuments = async (): Promise<LegalDocument[]> => {
  const { data } = await api.get(`${USER_BASE}/required`);
  return data.data.documents;
};

export const recordLegalAcceptance = async (payload: {
  legalDocumentId: string;
  action: LegalAcceptanceAction;
}) => {
  const { data } = await api.post("/api/legal-acceptance", payload);
  return data.data;
};

export interface LegalAcceptance {
  id: string;
  userId: string;
  legalDocumentId: string;
  documentType: LegalDocumentType;
  version: string | number;
  action: LegalAcceptanceAction;
  acceptedAt: string;
  createdAt: string;
}

export interface MyLegalAcceptancesResponse {
  acceptances: LegalAcceptance[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// The backend scopes this strictly to the authenticated user (req.user.userId);
// there is no way to request another user's history through this endpoint.
export const listMyLegalAcceptances = async (params?: {
  page?: number;
  limit?: number;
  documentType?: LegalDocumentType;
}): Promise<MyLegalAcceptancesResponse> => {
  const { data } = await api.get("/api/legal-acceptance/me", { params });
  return data.data;
};